import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';

/**
 * Full-screen image overlay viewer — dumb/presentational.
 *
 * Matches the Figma `IMG_Overlay` (node 16978-59399 chrome + 16978:62447
 * populated state): a dark blurred scrim (`black 0.8` + `white 0.16`), a
 * floating top-right circular white close button, and — centered — the image
 * shown fit-to-screen with the **file name as a caption directly beneath it**
 * (P3 / white / centered, 16px gap). No zoom, no download.
 * Built as a bespoke fixed overlay (not `sd-modal`) reusing
 * `sd-modal`'s `cdkTrapFocus`.
 *
 * Close funnels three ways — the X button, a click on the scrim (not the image
 * or caption), and Esc — all through `closeClicked`. The parent owns visibility
 * and the resolved URL.
 */
@Component({
  selector: 'squadbox-image-overlay-viewer',
  standalone: true,
  imports: [
    CdkTrapFocus,
    TranslateModule,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isVisible()) {
      <!-- Scrim = the flex container (so a click on its free space closes):
           black 0.8 + white 0.16 over a 6px backdrop blur (Figma 16978-59399).
           z-[999] = modal tier so it can't be occluded. Image + caption are
           centered as a group within the 80/56/80/80 fit margins. -->
      <div
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        role="dialog"
        [attr.aria-modal]="true"
        [attr.aria-label]="filename()"
        tabindex="-1"
        class="
          sd-image-overlay-scrim
          fixed inset-0 z-[999]
          flex flex-col items-center justify-center gap-4
          px-20 pt-20 pb-14
          backdrop-blur-[6px]
        "
        (click)="onScrimClick($event)"
        (keydown.escape)="closeClicked.emit()"
      >
        <!-- Floating close: 40px circle, 24px white icon, 16px inset
             (Figma 16978:59400). White forced on the svg because the
             design-system icon-button has no white-on-dark color variant. -->
        <button
          sdIconButton
          variant="icon"
          color="neutral"
          size="sm"
          class="absolute right-4 top-4"
          [attr.aria-label]="'CHAT.IMAGE_OVERLAY.CLOSE' | translate"
          (click)="closeClicked.emit()"
        >
          <svg sdUseUIBasicIcon="close" class="text-shades-white"></svg>
        </button>

        @if (hasError() || loadError()) {
          <span class="typo-p1 text-shades-white">
            {{ 'CHAT.IMAGE_OVERLAY.LOAD_ERROR' | translate }}
          </span>
        } @else if (src()) {
          <img
            [src]="src()"
            [alt]="filename()"
            (error)="hasError.set(true)"
            class="min-h-0 max-h-full max-w-full object-contain"
          />
          <span
            class="max-w-full shrink-0 break-words text-center typo-p3 text-shades-white"
            >{{ filename() }}</span
          >
        }
      </div>
    }
  `,
  styles: [
    `
      .sd-image-overlay-scrim {
        background:
          linear-gradient(rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0.16)),
          rgba(0, 0, 0, 0.8);
      }
    `,
  ],
})
export class ImageOverlayViewerComponent {
  public readonly isVisible = input.required<boolean>();
  public readonly src = input<string | null>(null);
  public readonly filename = input<string>('');
  /**
   * Smart-driven error: the parent could not resolve a URL (e.g. the presigned
   * request failed). Distinct from `hasError`, the local `<img>`-load failure —
   * a null/empty `src` never fires that.
   */
  public readonly loadError = input<boolean>(false);

  public readonly closeClicked = output<void>();

  /** Set when the <img> fails to load; shows the generic error state. */
  public readonly hasError = signal(false);

  constructor() {
    // Reset the error state whenever a new image is shown.
    effect(() => {
      this.src();
      this.hasError.set(false);
    });
  }

  /**
   * Close only when the click lands on the scrim itself — not on the image,
   * the caption, or the close button (mirrors `sd-modal`'s backdrop-dismiss
   * guard).
   */
  public onScrimClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeClicked.emit();
    }
  }
}
